// src/components/resources/quizzes/components/QuestionOptions.tsx

'use client'

import { cn } from '~/lib/utils'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import type { QuestionOptionsProps } from '~/components/resources/quizzes/types/quiz-types'

export function QuestionOptions({
  question,
  selectedAnswer,
  onAnswerSelect,
  showFeedback,
  disabled
}: QuestionOptionsProps): JSX.Element {
  const isMultiValue = question.type === 'multiselect'
  const isOrdered = question.type === 'ordered'
  const orderedAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : []

  const handleOptionClick = (optionId: string): void => {
    if (disabled && !isOrdered) return

    if (isMultiValue) {
      const currentAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : []
      const newAnswers = currentAnswers.includes(optionId)
        ? currentAnswers.filter(id => id !== optionId)
        : [...currentAnswers, optionId]
      onAnswerSelect(newAnswers)
    } else if (isOrdered) {
      const currentAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : []
      if (currentAnswers.includes(optionId)) {
        // Remove this and all subsequent items
        const index = currentAnswers.indexOf(optionId)
        onAnswerSelect(currentAnswers.slice(0, index))
      } else {
        // Add to the sequence
        onAnswerSelect([...currentAnswers, optionId])
      }
    } else {
      onAnswerSelect(optionId)
    }
  }

  const renderOption = (option: typeof question.options[0]): JSX.Element => {
    const isSelected = isMultiValue || isOrdered
      ? (Array.isArray(selectedAnswer) && selectedAnswer.includes(option.id))
      : selectedAnswer === option.id

    // const isCorrect = isMultiValue
    //   ? Array.isArray(question.correctAnswer) && question.correctAnswer.includes(option.id)
    //   : option.id === question.correctAnswer

    const orderIndex = isOrdered ? orderedAnswers.indexOf(option.id) : -1
    const correctOrderIndex = isOrdered && Array.isArray(question.correctAnswer) 
      ? question.correctAnswer.indexOf(option.id)
      : -1
    const isInSequence = orderIndex !== -1
    const isInCorrectPosition = isOrdered && isInSequence && orderIndex === correctOrderIndex

    // For ordered questions, an option is correct only if it's in the right position
    const optionIsCorrect = isOrdered
      ? isInCorrectPosition
      : isMultiValue
        ? (Array.isArray(question.correctAnswer) && question.correctAnswer.includes(option.id))
        : option.id === question.correctAnswer

    // Show feedback colors based on showFeedback prop
    const showFeedbackColors = showFeedback && (
      (isOrdered && isInSequence && !isInCorrectPosition) ||
      (!isOrdered && isSelected && !optionIsCorrect) ||
      (showFeedback && optionIsCorrect)
    )

    return (
      <div key={option.id} className="space-y-2">
        <button
          onClick={() => handleOptionClick(option.id)}
          disabled={disabled && !isOrdered}
          className={cn(
            "group relative w-full rounded-xl border px-4 py-3 text-left transition-all",
            isSelected
              ? "border-indigo-600 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/20"
              : "border-gray-200 bg-white hover:border-indigo-200 hover:bg-indigo-50/50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-800 dark:hover:bg-indigo-900/10",
            disabled && !isOrdered && "cursor-not-allowed opacity-50",
            showFeedbackColors && isOrdered && isInSequence && (
              isInCorrectPosition
                ? "border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-900/20"
                : "border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-900/20"
            ),
            showFeedbackColors && !isOrdered && (
              optionIsCorrect
                ? "border-green-500 bg-green-50 dark:border-green-400 dark:bg-green-900/20"
                : isSelected ? "border-red-500 bg-red-50 dark:border-red-400 dark:bg-red-900/20" : ""
            )
          )}
        >
          <div className="flex items-center gap-3">
            {/* Selection Indicator */}
            <div className={cn(
              "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-sm transition-colors",
              isOrdered ? (
                isInSequence
                  ? showFeedback
                    ? isInCorrectPosition
                      ? "border-green-500 bg-green-500 text-white dark:border-green-400 dark:bg-green-400"
                      : "border-red-500 bg-red-500 text-white dark:border-red-400 dark:bg-red-400"
                    : "border-indigo-600 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-400"
                  : "border-gray-300 dark:border-gray-600"
              ) : (
                isSelected
                  ? showFeedback
                    ? optionIsCorrect
                      ? "border-green-500 bg-green-500 text-white dark:border-green-400 dark:bg-green-400"
                      : "border-red-500 bg-red-500 text-white dark:border-red-400 dark:bg-red-400"
                    : "border-indigo-600 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-400"
                  : "border-gray-300 dark:border-gray-600"
              )
            )}>
              {isOrdered ? (
                isInSequence ? (
                  <span className={cn(
                    "text-xs",
                    showFeedback ? (isInCorrectPosition ? "text-white" : "text-red-100") : "text-white"
                  )}>
                    {orderIndex + 1}
                  </span>
                ) : (
                  <span className="text-xs">+</span>
                )
              ) : (
                isSelected ? (
                  showFeedback && !optionIsCorrect ? (
                    <RiCloseLine className="h-3 w-3" />
                  ) : (
                    <RiCheckLine className="h-3 w-3" />
                  )
                ) : (
                  showFeedback && optionIsCorrect && <RiCheckLine className="h-3 w-3" />
                )
              )}
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-sm font-medium",
                  isSelected
                    ? "text-indigo-900 dark:text-indigo-100"
                    : "text-gray-900 dark:text-white",
                  showFeedback && isOrdered && isInSequence && (
                    isInCorrectPosition
                      ? "text-green-900 dark:text-green-100"
                      : "text-red-900 dark:text-red-100"
                  ),
                  showFeedback && !isOrdered && (
                    optionIsCorrect
                      ? isSelected ? "text-green-900 dark:text-green-100" : ""
                      : isSelected ? "text-red-900 dark:text-red-100" : ""
                  )
                )}>
                  {option.label}
                </span>
                {/* Show correct position indicator next to text instead of in circle */}
                {showFeedback && isOrdered && isInSequence && !isInCorrectPosition && (
                  <span className="text-xs text-red-500 dark:text-red-400">
                    (should be position {correctOrderIndex + 1})
                  </span>
                )}
              </div>
              {option.description && (
                <p className={cn(
                  "mt-1 text-sm",
                  isSelected
                    ? "text-indigo-700 dark:text-indigo-300"
                    : "text-gray-600 dark:text-gray-400",
                  showFeedback && isOrdered && isInSequence && (
                    isInCorrectPosition
                      ? "text-green-700 dark:text-green-300"
                      : "text-red-700 dark:text-red-300"
                  ),
                  showFeedback && !isOrdered && (
                    optionIsCorrect
                      ? isSelected ? "text-green-700 dark:text-green-300" : ""
                      : isSelected ? "text-red-700 dark:text-red-300" : ""
                  )
                )}>
                  {option.description}
                </p>
              )}
            </div>
          </div>
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {isOrdered && !showFeedback && orderedAnswers.length > 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click an item to remove it and all subsequent items from the sequence.
        </p>
      )}
      {isOrdered && showFeedback && !orderedAnswers.every((id, index) => id === question.correctAnswer[index]) && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">
          The order is incorrect. Numbers show current position, with corrections noted.
        </p>
      )}
      {question.options.map(renderOption)}
    </div>
  )
} 